/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import {
    Container, Box, Text, Grid, Button, Divider
} from 'components/ui';
import Barcode from 'components/Barcode/Barcode';
import Banner from 'components/Content/Banner';
import Copy from 'components/Content/Copy';
import contentConstants from 'constants/content';
import Action from 'components/Content/Action';
import dateUtils from 'utils/Date';
import Markdown from 'components/Markdown/Markdown';
import rwdAdvocacyPageBindings from 'analytics/bindingMethods/pages/rwdAdvocacy/rwdAdvocacyPageBindings';

const ActionButton = Action(Button);
const { BANNER_TYPES } = contentConstants;

// OPT-23062: Fixed max width for the enhanced Success view's Shop Now CTA (matches Figma).
const SHOP_NOW_MAX_WIDTH = 340;

const formatOrdinal = (isoStr, includeYear) => (isoStr ? dateUtils.getDateInShortMonthOrdinalFormat(isoStr.substr(0, 10), includeYear) : null);
const formatShortMonth = (isoStr, includeYear) => (isoStr ? dateUtils.getDateInMDDShortMonthFormat(isoStr.substr(0, 10), includeYear) : null);
const capitalize = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

class AdvocacySuccess extends BaseClass {
    componentDidMount() {
        rwdAdvocacyPageBindings.setPageLoadAnaytics(this.props.pageType);
    }

    renderEnhanced() {
        const {
            seo,
            locales: {
                barcodeScan,
                redemptionInstructions,
                shopNow,
                yourNextPurchase,
                redeemOnline,
                valid,
                promoReminder
            },
            globalHeroBanner,
            globalInvitationText,
            successConfirmationText,
            successInstructions,
            successDiscountDescription,
            successDisclaimer,
            successCtaLink,
            successPromoStartDate,
            successPromoEndDate,
            successCouponCode,
            apiCouponCode,
            referrerFirstName
        } = this.props;

        const firstNameCap = capitalize(this.props.user?.firstName);
        const startOrdinal = formatOrdinal(successPromoStartDate, false);
        const endOrdinal = formatOrdinal(successPromoEndDate, true);
        // OPT-23062: prefer per-user coupon from RMS (validate/addToPromotion) over the static
        // Contentful placeholder. Contentful stays as the fallback for pre-migration campaigns.
        const effectiveCouponCode = apiCouponCode || successCouponCode;
        const promoWindow = startOrdinal && endOrdinal ? `${startOrdinal} – ${endOrdinal}` : null;

        return (
            <Container
                paddingX={[0, 4]}
                paddingBottom={0}
            >
                <Box
                    paddingX={[4, 0]}
                    paddingTop={[0, 6]}
                >
                    <Grid
                        columns={[0, '2fr 3fr']}
                        gap={0}
                        alignItems='start'
                    >
                        <Box>
                            {globalHeroBanner?.[0]?.media?.src && (
                                <Banner
                                    media={globalHeroBanner[0].media}
                                    largeMedia={globalHeroBanner[0].largeMedia}
                                    bannerType={BANNER_TYPES.HERO}
                                    seoHeader={seo?.header1}
                                    marginTop={0}
                                    marginBottom={[5, 0]}
                                    alignLeft
                                    enablePageRenderTracking={true}
                                />
                            )}
                        </Box>
                        <Box marginTop={[5, 0]}>
                            <Box marginLeft={[0, 7]}>
                                <Text
                                    is='p'
                                    data-at={Sephora.debug.dataAt('referrer_text')}
                                    children={`${globalInvitationText} ${referrerFirstName}`}
                                />
                                {successConfirmationText && (
                                    <Text
                                        is='p'
                                        fontWeight='bold'
                                        fontSize='lg'
                                        marginTop={2}
                                        data-at={Sephora.debug.dataAt('promo_confirmation')}
                                        children={`${firstNameCap}${successConfirmationText}`}
                                    />
                                )}
                                {successInstructions && (
                                    <Copy
                                        content={successInstructions}
                                        data-at={Sephora.debug.dataAt('redemption_instructions')}
                                        marginTop={2}
                                        marginBottom={0}
                                    />
                                )}
                                {successDiscountDescription && (
                                    <Markdown
                                        fontWeight='bold'
                                        fontSize='2xl'
                                        marginTop={5}
                                        data-at={Sephora.debug.dataAt('promo_discount')}
                                        content={successDiscountDescription}
                                    />
                                )}
                                <Text
                                    is='p'
                                    fontWeight='bold'
                                    children={yourNextPurchase}
                                    marginTop={1}
                                />
                                {promoWindow && (
                                    <Text
                                        is='p'
                                        data-at={Sephora.debug.dataAt('promo_validity')}
                                        children={`${valid} ${promoWindow}`}
                                        marginTop={1}
                                    />
                                )}
                                <Text
                                    is='h4'
                                    fontWeight='bold'
                                    marginTop={5}
                                    children={`${redemptionInstructions}:`}
                                />
                                {promoWindow && (
                                    <Text
                                        is='p'
                                        marginTop={2}
                                        data-at={Sephora.debug.dataAt('promo_reminder')}
                                    >
                                        {`⚠️ ${promoReminder} `}
                                        <Text
                                            is='span'
                                            fontWeight='bold'
                                            children={promoWindow}
                                        />
                                    </Text>
                                )}
                                {effectiveCouponCode && (
                                    <Text
                                        is='p'
                                        marginTop={2}
                                    >
                                        {`${redeemOnline} `}
                                        <Text
                                            is='span'
                                            fontWeight='bold'
                                            children={effectiveCouponCode}
                                        />
                                    </Text>
                                )}
                                {effectiveCouponCode && (
                                    <>
                                        <Text
                                            is='p'
                                            marginTop={2}
                                            marginBottom={4}
                                            children={barcodeScan}
                                        />
                                        <Barcode
                                            barcodeDataAt='promo_barcode'
                                            code={'CODE39'}
                                            id={effectiveCouponCode}
                                            hasBorder={true}
                                            flexDirection='row'
                                            isCenter={false}
                                        />
                                    </>
                                )}
                                <ActionButton
                                    variant='primary'
                                    children={shopNow}
                                    width='100%'
                                    marginTop={5}
                                    data-at={Sephora.debug.dataAt('shop_now_btn')}
                                    maxWidth={['100%', SHOP_NOW_MAX_WIDTH]}
                                    action={successCtaLink}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    {successDisclaimer && (
                        <Grid
                            columns={[0, '2fr 3fr']}
                            gap={0}
                        >
                            <Box />
                            <Box>
                                <Box marginLeft={[0, 7]}>
                                    <Divider
                                        marginTop={[6, 7]}
                                        marginBottom={[5, 4]}
                                    />
                                    <Box
                                        fontSize='sm'
                                        color='gray'
                                    >
                                        <Copy
                                            content={successDisclaimer}
                                            data-at={Sephora.debug.dataAt('offer_disclaimer')}
                                            marginTop={0}
                                            marginBottom={0}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Box>
            </Container>
        );
    }

    render() {
        if (this.props.isEnhanced) {
            return this.renderEnhanced();
        }

        const {
            seo,
            locales: {
                barcodeScan, redemptionInstructions, shopNow, yourNextPurchase, redeemOnline, valid
            },
            globalHeroBanner,
            globalInvitationText,
            successConfirmationText,
            successInstructions,
            successDiscountDescription,
            successDisclaimer,
            successCtaLink,
            successPromoStartDate,
            successPromoEndDate,
            successCouponCode,
            referrerFirstName
        } = this.props;

        const firstNameCap = capitalize(this.props.user?.firstName);
        const parsedPromoStartDate = formatShortMonth(successPromoStartDate, false);
        const parsedPromoEndDate = formatShortMonth(successPromoEndDate, true);

        return (
            <Container
                paddingX={[0, 4]}
                paddingBottom={0}
            >
                {globalHeroBanner?.[0] && (
                    <Banner
                        {...globalHeroBanner[0]}
                        bannerType={BANNER_TYPES.HERO}
                        seoHeader={seo?.header1}
                        marginTop={[0, 5]}
                        marginBottom={[6, 7]}
                        alignLeft
                        enablePageRenderTracking={true}
                    />
                )}
                <Box paddingX={[4, 0]}>
                    <Grid
                        columns={[0, 2]}
                        gap={0}
                    >
                        <Box paddingRight={[0, 1]}>
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('referrer_text')}
                                children={`${globalInvitationText} ${referrerFirstName}`}
                            />
                            {successConfirmationText && (
                                <Text
                                    is='p'
                                    fontWeight='bold'
                                    fontSize='2xl'
                                    marginTop={5}
                                    data-at={Sephora.debug.dataAt('promo_confirmation')}
                                    children={`${firstNameCap}${successConfirmationText}`}
                                />
                            )}
                            {successInstructions && (
                                <Copy
                                    content={successInstructions}
                                    data-at={Sephora.debug.dataAt('redemption_instructions')}
                                    marginTop={[4, 3]}
                                    marginBottom={0}
                                />
                            )}
                            {successDiscountDescription && (
                                <Markdown
                                    fontWeight='bold'
                                    fontSize='2xl'
                                    marginTop={5}
                                    data-at={Sephora.debug.dataAt('promo_discount')}
                                    content={successDiscountDescription}
                                />
                            )}
                            <Text
                                is='p'
                                fontWeight='bold'
                                children={yourNextPurchase}
                                marginTop={1}
                            />
                            {parsedPromoStartDate && parsedPromoEndDate && (
                                <Text
                                    is='p'
                                    data-at={Sephora.debug.dataAt('promo_validity')}
                                    children={`${valid} ${parsedPromoStartDate} - ${parsedPromoEndDate}`}
                                    marginTop={1}
                                />
                            )}
                        </Box>
                        <Box
                            borderLeft={[0, '1px solid lightGray']}
                            marginTop={[5, 0]}
                        >
                            <Box marginLeft={[0, 7]}>
                                <Text
                                    is='h4'
                                    fontWeight='bold'
                                    children={`${redemptionInstructions}:`}
                                />
                                <Text
                                    is='p'
                                    marginTop={2}
                                >
                                    {`${redeemOnline} `}
                                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{successCouponCode}</span>
                                </Text>
                                <Text
                                    is='p'
                                    marginTop={2}
                                    marginBottom={4}
                                    children={barcodeScan}
                                />
                                <Barcode
                                    barcodeDataAt='promo_barcode'
                                    code={'CODE39'}
                                    id={successCouponCode}
                                    hasBorder={true}
                                    flexDirection='row'
                                    isCenter={false}
                                />
                                <ActionButton
                                    variant='primary'
                                    children={shopNow}
                                    width='100%'
                                    marginTop={5}
                                    data-at={Sephora.debug.dataAt('shop_now_btn')}
                                    maxWidth={['100%', 276]}
                                    action={successCtaLink}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    {successDisclaimer && (
                        <>
                            <Divider
                                marginTop={[6, 7]}
                                marginBottom={[5, 4]}
                            />
                            <Copy
                                content={successDisclaimer}
                                marginTop={0}
                                marginBottom={0}
                            />
                        </>
                    )}
                </Box>
            </Container>
        );
    }
}

export default wrapComponent(AdvocacySuccess, 'AdvocacySuccess', true);
